import { useRef, useState, DragEvent, ChangeEvent, CSSProperties } from 'react';
import { IconUpload, IconCamera } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { V4 } from '../../../tokens';
import { ACCEPTED_PDF_MIME, ACCEPTED_IMAGE_MIMES } from '../constants';
import { compressImages } from '../utils/compressImage';

export interface MultiFileUploadZoneProps {
  onFilesAdded: (files: File[]) => void;
  currentPdfCount: number;
  currentImageCount: number;
  currentTotalMB: number;
  maxPdfs: number;
  maxImages: number;
  maxFileMB: number;
  maxTotalMB: number;
  disabled?: boolean;
}

type AcceptedImageMime = (typeof ACCEPTED_IMAGE_MIMES)[number];

const ACCEPT_ATTR = [ACCEPTED_PDF_MIME, ...ACCEPTED_IMAGE_MIMES].join(',');

function isAcceptedMime(mime: string): boolean {
  if (mime === ACCEPTED_PDF_MIME) return true;
  return (ACCEPTED_IMAGE_MIMES as readonly string[]).includes(mime);
}

function isImageMime(mime: string): mime is AcceptedImageMime {
  return (ACCEPTED_IMAGE_MIMES as readonly string[]).includes(mime);
}

function bytesToMB(bytes: number): number {
  return bytes / (1024 * 1024);
}

export function MultiFileUploadZone({
  onFilesAdded,
  currentPdfCount,
  currentImageCount,
  currentTotalMB,
  maxPdfs,
  maxImages,
  maxFileMB,
  maxTotalMB,
  disabled = false,
}: MultiFileUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [primaryHover, setPrimaryHover] = useState(false);
  const [secondaryHover, setSecondaryHover] = useState(false);

  const pdfRatio = maxPdfs > 0 ? currentPdfCount / maxPdfs : 0;
  const imageRatio = maxImages > 0 ? currentImageCount / maxImages : 0;
  const totalRatio = maxTotalMB > 0 ? currentTotalMB / maxTotalMB : 0;
  const maxRatio = Math.max(pdfRatio, imageRatio, totalRatio);
  const counterColor = maxRatio >= 1 ? V4.danger : maxRatio >= 0.8 ? V4.warning : V4.grayUltraDark;

  const processIncomingFiles = async (incoming: File[]): Promise<void> => {
    if (disabled) return;
    if (incoming.length === 0) return;

    // === Compresión client-side antes de validar tamaño ===
    // Reduce imágenes > 2MB a ~1.5MB target (cap 2400px lado mayor, JPEG q=0.82).
    // PDFs y archivos pequeños pasan intactos. Si el browser no soporta canvas,
    // o el navegador rechaza decodificar, también pasa intacto (degrade graceful).
    const compressionResults = await compressImages(incoming);
    let compressedCount = 0;
    let savedBytes = 0;
    const processed = compressionResults.map((r) => {
      if (r.compressed) {
        compressedCount += 1;
        savedBytes += r.originalBytes - r.finalBytes;
      }
      return r.file;
    });
    if (compressedCount > 0) {
      const savedMB = (savedBytes / (1024 * 1024)).toFixed(1);
      toast.success(
        `Optimizamos ${compressedCount} ${compressedCount === 1 ? 'imagen' : 'imágenes'} (${savedMB} MB menos) para acelerar.`,
        { id: 'smart-menu-compression' },
      );
    }

    const accepted: File[] = [];
    const rejectedByMime: string[] = [];
    const rejectedBySize: string[] = [];
    let pdfRoom = Math.max(0, maxPdfs - currentPdfCount);
    let imageRoom = Math.max(0, maxImages - currentImageCount);
    let totalMBLeft = Math.max(0, maxTotalMB - currentTotalMB);
    let droppedPdfCap = 0;
    let droppedImageCap = 0;
    let droppedTotalCap = 0;

    processed.forEach((file) => {
      if (!isAcceptedMime(file.type)) {
        rejectedByMime.push(file.name);
        return;
      }
      const sizeMB = bytesToMB(file.size);
      if (sizeMB > maxFileMB) {
        rejectedBySize.push(file.name);
        return;
      }

      const isPdf = file.type === ACCEPTED_PDF_MIME;
      if (isPdf) {
        if (pdfRoom <= 0) {
          droppedPdfCap += 1;
          return;
        }
      } else if (isImageMime(file.type)) {
        if (imageRoom <= 0) {
          droppedImageCap += 1;
          return;
        }
      }

      if (sizeMB > totalMBLeft) {
        droppedTotalCap += 1;
        return;
      }

      accepted.push(file);
      totalMBLeft -= sizeMB;
      if (isPdf) {
        pdfRoom -= 1;
      } else {
        imageRoom -= 1;
      }
    });

    if (rejectedByMime.length > 0) {
      toast.error(
        rejectedByMime.length === 1
          ? `Formato no soportado: ${rejectedByMime[0]}`
          : `${rejectedByMime.length} archivos con formato no soportado`,
      );
    }
    if (rejectedBySize.length > 0) {
      toast.error(
        rejectedBySize.length === 1
          ? `Archivo supera ${maxFileMB} MB: ${rejectedBySize[0]}`
          : `${rejectedBySize.length} archivos superan ${maxFileMB} MB`,
      );
    }
    if (droppedPdfCap > 0) {
      toast(`Solo aceptamos ${maxPdfs} PDFs. ${droppedPdfCap} quedaron fuera.`);
    }
    if (droppedImageCap > 0) {
      toast(`Solo aceptamos ${maxImages} imágenes. ${droppedImageCap} quedaron fuera.`);
    }
    if (droppedTotalCap > 0) {
      toast(`Límite total ${maxTotalMB} MB alcanzado. ${droppedTotalCap} archivos quedaron fuera.`);
    }

    if (accepted.length > 0) {
      onFilesAdded(accepted);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    processIncomingFiles(files).catch(() => undefined);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    processIncomingFiles(files).catch(() => undefined);
    e.target.value = '';
  };

  const openFilePicker = (): void => {
    if (!disabled) fileInputRef.current?.click();
  };

  const openCameraPicker = (): void => {
    if (!disabled) cameraInputRef.current?.click();
  };

  const dropzoneBorder = disabled ? V4.grayLight : isDragOver ? V4.secondary : V4.grayLight;

  const dropzoneStyle: CSSProperties = {
    border: `2px dashed ${dropzoneBorder}`,
    background: V4.grayUltraLight,
    borderRadius: 12,
    padding: 32,
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'border-color 150ms ease, background 150ms ease',
    gap: 12,
  };

  const primaryBtnStyle: CSSProperties = {
    background: primaryHover && !disabled ? V4.secondaryDark : V4.secondary,
    color: V4.white,
    border: `1.5px solid ${primaryHover && !disabled ? V4.secondaryDark : V4.secondary}`,
    borderRadius: 6,
    padding: '10px 20px',
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background 150ms ease',
  };

  const secondaryBtnStyle: CSSProperties = {
    background: secondaryHover && !disabled ? V4.grayUltraLight : V4.white,
    color: V4.primary,
    border: `1.5px solid ${V4.primary}`,
    borderRadius: 6,
    padding: '10px 20px',
    fontWeight: 700,
    fontSize: 13,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    transition: 'background 150ms ease',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        data-testid="multifile-upload-counter"
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: counterColor,
          textAlign: 'left',
        }}
      >
        {`PDFs ${currentPdfCount}/${maxPdfs} · Imágenes ${currentImageCount}/${maxImages} · Total ${currentTotalMB.toFixed(1)}/${maxTotalMB} MB`}
      </div>

      <div
        data-testid="multifile-upload-zone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label="Zona para subir archivos del menú"
        onClick={openFilePicker}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFilePicker();
          }
        }}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={dropzoneStyle}
      >
        <IconUpload size={40} color={V4.secondary} stroke={1.6} aria-hidden="true" />

        <h3
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
            color: V4.primary,
          }}
        >
          Sube tu menú actual
        </h3>

        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: V4.grayUltraDark,
            maxWidth: 460,
            lineHeight: 1.4,
          }}
        >
          Lo digitalizamos por ti en segundos. Suelta fotos del menú impreso o el PDF que ya tengas. Hasta {maxPdfs}{' '}
          PDFs y {maxImages} imágenes.
        </p>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 12,
            marginTop: 4,
          }}
        >
          <button
            type="button"
            data-testid="multifile-upload-cta-primary"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              openFilePicker();
            }}
            onMouseEnter={() => setPrimaryHover(true)}
            onMouseLeave={() => setPrimaryHover(false)}
            style={primaryBtnStyle}
          >
            <IconUpload size={16} aria-hidden="true" />
            Elegir archivos
          </button>

          <button
            type="button"
            data-testid="multifile-upload-cta-camera"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              openCameraPicker();
            }}
            onMouseEnter={() => setSecondaryHover(true)}
            onMouseLeave={() => setSecondaryHover(false)}
            style={secondaryBtnStyle}
          >
            <IconCamera size={16} aria-hidden="true" />
            Tomar foto
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
        />

        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
